import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { useGetPhonicsSoundsQuery } from '@/api/types';
import { SymbolType, CdnObjectType } from '@/types';
import { CdnMedia } from '@/components';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Spell } from '../components/Spell';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';
import { Toolbar } from '../components/Toolbar';

interface SpellWordProps {
  word: string;
}

const SpellWord = ({ word }: SpellWordProps) => {
  const spellRef = useRef<Spell>(null);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [imageShowing, setImageShowing] = useState<boolean>(false);

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  return (
    <>
      <Toolbar
        onPlay={() => {
          spellRef.current?.play();
          setPlayedOnce(true);
        }}
        hintPlay={!playedOnce}
      />

      <WhitePanel mode="basic" kind="fill" color="paper">
        <CdnMedia
          className={clsx(
            'block mx-auto w-auto h-0 bg-white rounded-xl',
            'opacity-0 transition-all duration-300 ease-in-out',
            imageShowing && 'h-72 opacity-100',
          )}
          symbol={word}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />

        <Spell
          ref={spellRef}
          input={word}
          sounds={soundsData?.getPhonicsSounds.map((sound) => sound.name) || []}
          onEnded={() => setImageShowing(true)}
        />
      </WhitePanel>
    </>
  );
};

export const ReadingTrickyWords = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words || !activity.words2) {
    throw new Error('Words are required for this activity');
  }

  const { words } = activity;

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          parrotType="desk"
          wrapperClassName={wrapperClassName}
          isPreview={isPreview}
          index={0}
          total={words.length + 1}
        />,
        ...words.map((word, index) => (
          <ActivityStep
            key={`${activity.id}-${index}-1`}
            stepIdentifier={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            stepNumber={1}
            totalSteps={words.length + 1}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            usePanel
            title={`Say the sounds and blend them to make words.`}
            showTitle={true}
            showActivityPagination={false}
          >
            <SpellWord word={word} />
          </ActivityStep>
        )),
      ]}
    />
  );
};
